import {
  ref, nextTick, onBeforeUnmount, unref,
} from 'vue';

const VIEWPORT_MARGIN = 8;
const ARROW_MARGIN = 10;

// One anchored popover open app-wide: opening any closes the rest.
export const activePopoverId = ref(null);

function toElement(maybeRef) {
  const value = unref(maybeRef);
  return value?.$el ?? value ?? null;
}

// Anchored popover placement extracted from General/Info.vue: Teleport to body,
// fixed above the trigger, flipped below and clamped inside the viewport.
export function useAnchoredPopover(triggerRef, popoverRef, options = {}) {
  const {
    openDelay = 120,
    closeDelay = 120,
    gap = 8, // Info.vue's original 8px offset above the trigger
    fallbackWidth = 256, // w-64, until the popover has rendered and can be measured
  } = options;

  const isOpen = ref(false);
  const style = ref({});
  const placement = ref('above');
  const arrowOffset = ref(fallbackWidth / 2); // px from the popover's left edge to the trigger center
  let openTimer = null;
  let closeTimer = null;

  function clearTimers() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    openTimer = null;
    closeTimer = null;
  }

  function updatePosition() {
    const trigger = toElement(triggerRef);
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const popover = toElement(popoverRef);
    const width = popover?.offsetWidth || fallbackWidth;
    const height = popover?.offsetHeight || 0;

    let left = rect.left + rect.width / 2 - width / 2;
    const maxLeft = Math.max(window.innerWidth - width - VIEWPORT_MARGIN, VIEWPORT_MARGIN);
    left = Math.min(Math.max(left, VIEWPORT_MARGIN), maxLeft);
    arrowOffset.value = Math.min(Math.max(rect.left + rect.width / 2 - left, ARROW_MARGIN), width - ARROW_MARGIN);

    // Preferred placement is above; flip below when there is no room.
    const fitsAbove = rect.top - gap - height >= VIEWPORT_MARGIN;
    if (fitsAbove || height === 0) {
      placement.value = 'above';
      style.value = { left: `${left}px`, top: `${rect.top - gap}px`, transform: 'translateY(-100%)' };
    } else {
      placement.value = 'below';
      style.value = { left: `${left}px`, top: `${rect.bottom + gap}px`, transform: '' };
    }
  }

  function onReposition() {
    if (isOpen.value) updatePosition();
  }

  // Only the open popover listens; addEventListener dedupes repeat adds.
  function addRepositionListeners() {
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
  }

  function removeRepositionListeners() {
    window.removeEventListener('scroll', onReposition, true);
    window.removeEventListener('resize', onReposition);
  }

  function open() {
    clearTimers();
    isOpen.value = true;
    addRepositionListeners();
    updatePosition();
    // re-measure once the popover has actually rendered
    nextTick(updatePosition);
  }

  function close() {
    clearTimers();
    removeRepositionListeners();
    isOpen.value = false;
  }

  function scheduleOpen() {
    clearTimeout(closeTimer);
    closeTimer = null;
    if (isOpen.value) return;
    clearTimeout(openTimer);
    if (openDelay <= 0) {
      open();
      return;
    }
    openTimer = setTimeout(open, openDelay);
  }

  function scheduleClose() {
    clearTimeout(openTimer);
    openTimer = null;
    if (!isOpen.value) return;
    clearTimeout(closeTimer);
    if (closeDelay <= 0) {
      close();
      return;
    }
    closeTimer = setTimeout(close, closeDelay);
  }

  // Pointer entered the popover: keep it open.
  function cancelClose() {
    clearTimeout(closeTimer);
    closeTimer = null;
  }

  onBeforeUnmount(() => {
    clearTimers();
    removeRepositionListeners();
  });

  return {
    isOpen, style, placement, arrowOffset, open, close, scheduleOpen, scheduleClose, cancelClose, updatePosition,
  };
}
